import { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { brokerTransparency } from '../data';

const costCategories = [
  { key: 'Conversion markup', short: 'Conversion markup' },
  { key: 'Inactivity fees', short: 'Inactivity fee' },
  { key: 'Withdrawal fees', short: 'Withdrawal fee' },
  { key: 'Rollover widening', short: 'Rollover spread' },
  { key: 'Platform fees', short: 'Platform / data' },
  { key: 'Dividend adjustments', short: 'Dividend adj.' },
] as const;

type Meta = {
  stampLabel: string;
  stampBorder: string;
  stampText: string;
  paperTint: string;
};

function brokerMeta(idx: number): Meta {
  if (idx === 0) {
    return {
      stampLabel: 'Fully Itemized',
      stampBorder: 'border-primary-500/70',
      stampText: 'text-primary-600',
      paperTint: 'bg-background-50',
    };
  }
  if (idx === 1) {
    return {
      stampLabel: 'Buried in Spread',
      stampBorder: 'border-accent-500/70',
      stampText: 'text-accent-600',
      paperTint: 'bg-background-50',
    };
  }
  return {
    stampLabel: 'Partially Shown',
    stampBorder: 'border-secondary-500/70',
    stampText: 'text-secondary-600',
    paperTint: 'bg-background-50',
  };
}

export default function BrokerTransparencyScorecard() {
  const [inView, setInView] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [revealedLines, setRevealedLines] = useState<Set<string>>(new Set());
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Detect touch device for hint text
  useEffect(() => {
    const mq = window.matchMedia('(hover: none) and (pointer: coarse)');
    setIsTouchDevice(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsTouchDevice(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const toggleLine = useCallback((key: string) => {
    setRevealedLines((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  return (
    <section ref={sectionRef} className="w-full bg-background-100 py-12 md:py-18">
      <div className="max-w-main mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="max-w-[760px] mb-10 md:mb-12">
          <h2 className="text-[32px] md:text-[42px] font-[750] leading-[1.15] text-foreground-950 tracking-tight mb-4">
            Some brokers hand you the bill.
            <br className="hidden md:block" />
            {' '}
            Others bury it in the spread.
          </h2>
          <p className="text-[15px] leading-relaxed text-foreground-600">
            Here is what each broker model would print on your cost statement. The transparent one
            itemizes every charge. The opaque one blacks it out and folds it into the spread you accept.
          </p>
        </div>

        {/* Reveal hint — adapts wording for touch vs desktop */}
        <div className="flex items-center gap-2 mb-6 text-[13px] font-medium text-foreground-500">
          <span className="w-6 h-6 rounded-full bg-accent-100 flex items-center justify-center shrink-0">
            {isTouchDevice ? (
              <i className="ri-hand text-[13px] text-accent-600 w-3.5 h-3.5 flex items-center justify-center" />
            ) : (
              <i className="ri-cursor-line text-[13px] text-accent-600 w-3.5 h-3.5 flex items-center justify-center" />
            )}
          </span>
          {isTouchDevice ? (
            <>
              Tap any
              {' '}
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-foreground-950 text-background-50 text-[11px] font-semibold">
                <i className="ri-lock-2-line text-[11px] w-3 h-3 flex items-center justify-center" />
                redacted
              </span>
              {' '}
              line to expose the charge your broker hides. Tap again to re-cover it.
            </>
          ) : (
            <>
              Hover any
              {' '}
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-foreground-950 text-background-50 text-[11px] font-semibold">
                <i className="ri-lock-2-line text-[11px] w-3 h-3 flex items-center justify-center" />
                redacted
              </span>
              {' '}
              line to expose the charge your broker hides — or click to keep it revealed.
            </>
          )}
        </div>

        {/* ========== Receipts ========== */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6 items-start">
          {brokerTransparency.map((b, bIdx) => {
            const meta = brokerMeta(bIdx);
            const goodCount = Object.values(b.scores).filter((s) => s.level === 'good').length;
            const badCount = Object.values(b.scores).filter((s) => s.level === 'bad').length;

            return (
              <div
                key={b.brokerType}
                className="relative"
                style={{
                  opacity: inView ? 1 : 0,
                  transform: inView ? 'translateY(0) rotate(0deg)' : 'translateY(22px) rotate(-1deg)',
                  transitionDelay: `${bIdx * 130}ms`,
                  transitionProperty: 'opacity, transform',
                  transitionDuration: '650ms',
                  transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
                }}
              >
                {/* Paper */}
                <div
                  className={`${meta.paperTint} border-x border-t border-background-200 rounded-t-[6px] px-4 lg:px-5 pt-6 pb-5 font-mono`}
                  style={{ boxShadow: '0 1px 0 0 oklch(var(--background-200)) inset' }}
                >
                  {/* Statement header */}
                  <div className="text-center pb-4 border-b border-dashed border-foreground-300">
                    <div className="text-[10px] tracking-[0.25em] font-bold text-foreground-400 uppercase">
                      Cost Statement
                    </div>
                    <div className="mt-2 text-[15px] font-bold text-foreground-950 tracking-tight leading-snug">
                      {b.brokerType}
                    </div>
                    <div className="mt-1 text-[11px] text-foreground-500">{b.firms}</div>
                    <div className="mt-2.5 flex items-center justify-center gap-3 text-[10px] tracking-wide">
                      <span className="text-primary-600 font-semibold">
                        {goodCount}
                        {' '}
                        ITEMIZED
                      </span>
                      <span className="text-foreground-300">|</span>
                      <span className="text-accent-600 font-semibold">
                        {badCount}
                        {' '}
                        REDACTED
                      </span>
                    </div>
                  </div>

                  {/* Line items */}
                  <div className="py-3">
                    {costCategories.map((cat, cIdx) => {
                      const score = b.scores[cat.key as keyof typeof b.scores];
                      if (!score) return null;
                      const level = score.level as string;
                      const delay = bIdx * 130 + cIdx * 70 + 350;
                      const lineKey = `${bIdx}-${cIdx}`;
                      const isRevealed = revealedLines.has(lineKey);

                      return (
                        <div
                          key={cat.key}
                          className="group/line flex items-baseline gap-1 py-2 border-b border-dotted border-foreground-200 last:border-b-0 min-w-0"
                          style={{
                            opacity: inView ? 1 : 0,
                            transform: inView ? 'translateX(0)' : 'translateX(-6px)',
                            transitionDelay: `${delay}ms`,
                            transitionProperty: 'opacity, transform',
                            transitionDuration: '450ms',
                          }}
                        >
                          {/* Label */}
                          <span className="text-[12px] text-foreground-700 shrink-0 whitespace-nowrap">
                            {cat.short}
                          </span>
                          {/* Leader dots */}
                          <span className="flex-1 border-b border-dotted border-foreground-300 mx-1 translate-y-[-3px] min-w-[16px]" />

                          {/* Value */}
                          {level === 'good' && (
                            <span className="flex items-center gap-1 text-[11px] font-semibold text-primary-600 shrink-0 text-right whitespace-nowrap leading-tight">
                              <i className="ri-check-line text-[13px] w-3.5 h-3.5 flex items-center justify-center shrink-0" />
                              {score.rating}
                            </span>
                          )}

                          {level === 'medium' && (
                            <span className="flex items-center gap-1 text-[11px] italic text-secondary-600/80 shrink-0 text-right whitespace-nowrap leading-tight">
                              <i className="ri-subtract-line text-[12px] w-3 h-3 flex items-center justify-center shrink-0" />
                              {score.rating}
                            </span>
                          )}

                          {level === 'bad' && (
                            <button
                              type="button"
                              className="relative inline-block shrink-0 leading-tight whitespace-nowrap cursor-pointer touch-manipulation"
                              onClick={() => toggleLine(lineKey)}
                              aria-label={isRevealed ? `Hide ${cat.short} charge` : `Reveal hidden ${cat.short} charge`}
                            >
                              {/* Hidden text underneath */}
                              <span
                                className={`block text-[11px] font-semibold text-accent-700 text-right blur-[3px] select-none opacity-70 transition-all duration-300 group-hover/line:blur-0 group-hover/line:opacity-100 whitespace-nowrap ${
                                  isRevealed ? '!blur-0 !opacity-100' : ''
                                }`}
                              >
                                {score.rating}
                              </span>
                              {/* Redaction overlay */}
                              <span
                                className={`absolute inset-0 flex items-center justify-end gap-1 rounded-[3px] bg-foreground-950 px-1.5 text-[10px] font-bold text-background-50 transition-opacity duration-300 group-hover/line:opacity-0 whitespace-nowrap ${
                                  isRevealed ? '!opacity-0' : ''
                                }`}
                              >
                                <i className="ri-lock-2-line text-[11px] w-3 h-3 flex items-center justify-center" />
                                HIDDEN
                              </span>
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Divider */}
                  <div className="border-t border-dashed border-foreground-300 pt-4">
                    {/* Audit summary — replaces hardcoded score + stamp */}
                    <div className="mb-3 border border-dashed border-foreground-300 rounded-[4px] p-3 bg-background-50/50">
                      <div className="text-[9px] tracking-[0.2em] font-bold text-foreground-400 uppercase text-center">
                        Audit Summary
                      </div>
                      <div
                        className={`mt-1 text-[13px] font-bold text-center leading-tight ${
                          bIdx === 0
                            ? 'text-primary-600'
                            : bIdx === 1
                              ? 'text-accent-600'
                              : 'text-secondary-600'
                        }`}
                      >
                        {bIdx === 0 ? 'TRANSPARENT' : bIdx === 1 ? 'OPAQUE' : 'MIXED'}
                      </div>
                      <div className="mt-1 text-[10px] text-foreground-500 text-center tabular-nums">
                        {goodCount}
                        {' '}
                        clear
                        {' · '}
                        {costCategories.length - goodCount - badCount}
                        {' '}
                        partial
                        {' · '}
                        {badCount}
                        {' '}
                        hidden
                      </div>
                    </div>

                    {/* Headline quote */}
                    <div className="text-[12px] italic text-foreground-600 leading-relaxed text-center px-1">
                      &ldquo;
                      {b.headline}
                      &rdquo;
                    </div>
                  </div>
                </div>

                {/* Torn zigzag bottom edge */}
                <div
                  aria-hidden="true"
                  style={{
                    height: '11px',
                    backgroundImage:
                      'linear-gradient(135deg, oklch(var(--background-50)) 40%, transparent 40%), linear-gradient(-135deg, oklch(var(--background-50)) 40%, transparent 40%)',
                    backgroundSize: '14px 14px',
                    backgroundRepeat: 'repeat-x',
                    backgroundPosition: 'top left',
                    filter: 'drop-shadow(0 1px 0 oklch(var(--background-200)))',
                  }}
                />

                {/* Advisory note — the decision guidance the receipt cannot give you */}
                <div className="mt-4 mx-1 font-sans">
                  <div className="text-[10px] tracking-[0.2em] font-bold text-foreground-400 uppercase mb-2.5">
                    Advisor&rsquo;s Note
                  </div>
                  <dl className="space-y-2.5">
                    <div className="grid grid-cols-[64px_1fr] gap-2 items-baseline">
                      <dt className="text-[11px] font-bold text-primary-600 whitespace-nowrap">Best for</dt>
                      <dd className="text-[12px] text-foreground-600 leading-relaxed">{b.bestFor}</dd>
                    </div>
                    <div className="grid grid-cols-[64px_1fr] gap-2 items-baseline">
                      <dt className="text-[11px] font-bold text-accent-600 whitespace-nowrap">Avoid if</dt>
                      <dd className="text-[12px] text-foreground-600 leading-relaxed">{b.avoidIf}</dd>
                    </div>
                    <div className="grid grid-cols-[64px_1fr] gap-2 items-baseline">
                      <dt className="text-[11px] font-bold text-secondary-600 whitespace-nowrap">Do this</dt>
                      <dd className="text-[12px] text-foreground-600 leading-relaxed">
                        {bIdx === 0 ? (
                          <>
                            Ask support for the{' '}
                            <Link to="/forex-commission/" className="underline hover:text-primary-600">
                              commission-inclusive cost
                            </Link>{' '}
                            per standard lot in writing, then compare it head-to-head against a Market Maker&rsquo;s all-in spread on your most-traded pair.
                          </>
                        ) : bIdx === 1 ? (
                          <>
                            Pull one month of statements and check the{' '}
                            <Link to="/calculate-forex-trading-costs/" className="underline hover:text-primary-600">
                              FX rate applied to every P&L conversion
                            </Link>{' '}
                            against the mid-market rate at that timestamp — the gap is the cost they never printed.
                          </>
                        ) : bIdx === 2 ? (
                          <>
                            Set a calendar reminder to trade at least once every 90 days to dodge the inactivity fee, and place orders during peak-session hours so you route to the{' '}
                            <Link to="/forex-slippage/" className="underline hover:text-primary-600">
                              deeper liquidity pool
                            </Link>.
                          </>
                        ) : (
                          b.verify
                        )}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom summary box — The Verdict */}
        <div
          className="mt-10 md:mt-12 bg-background-50 rounded-card border border-background-200 p-5 md:p-6"
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(12px)',
            transitionDelay: '650ms',
            transitionProperty: 'opacity, transform',
            transitionDuration: '600ms',
          }}
        >
          {/* Label */}
          <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] font-bold text-foreground-400 uppercase mb-3">
            The Verdict
          </span>

          {/* Headline */}
          <h3 className="text-[22px] md:text-[26px] font-bold text-foreground-950 leading-[1.2] tracking-tight mb-3">
            A broker that hides costs
            <br className="hidden sm:block" />
            {' '}
            is a broker that keeps them.
          </h3>

          <p className="text-[14px] text-foreground-500 leading-relaxed mb-6 max-w-[680px]">
            After comparing what each model charges &mdash; and what they bury in the spread &mdash; the gap between the most transparent broker and the most opaque one is not a rounding error. It is the difference between trading with an edge and trading blind.
          </p>

          {/* Three rules */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            <div className="flex gap-2.5">
              <span className="text-[28px] font-bold text-primary-500/25 leading-none shrink-0 -mt-1 select-none">01</span>
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-foreground-900 mb-1">Itemized = negotiable</div>
                <p className="text-[12px] text-foreground-500 leading-relaxed">
                  If it shows up as a line item, you can compare it across brokers, ask for a better rate, or switch. Hidden costs strip away your leverage.
                </p>
              </div>
            </div>

            <div className="flex gap-2.5">
              <span className="text-[28px] font-bold text-accent-500/25 leading-none shrink-0 -mt-1 select-none">02</span>
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-foreground-900 mb-1">Cross-currency kills</div>
                <p className="text-[12px] text-foreground-500 leading-relaxed">
                  A trader on EURGBP with a USD account loses 3&ndash;5&times; more to conversion markup with a Market Maker than an ECN &mdash; and the statement will never show it.
                </p>
              </div>
            </div>

            <div className="flex gap-2.5">
              <span className="text-[28px] font-bold text-secondary-500/25 leading-none shrink-0 -mt-1 select-none">03</span>
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-foreground-900 mb-1">Audit once a year</div>
                <p className="text-[12px] text-foreground-500 leading-relaxed">
                  One afternoon pulling statements and checking rates catches leaks that compound into thousands. The checklist above is your audit playbook.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}